/**
 * Simple Room Canvas App - Furniture Removal Tool
 * Basic image upload, rotate, crop, and AI processing
 */

import React, { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import './App.modern-minimal.css';

// Simple processing result interface
interface ProcessingResult {
  downloadUrl: string;
  processingTimeMs: number;
}

const APP_VERSION = process.env.REACT_APP_VERSION || '1.27.162';

function App() {
  // Core states
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [processedImage, setProcessedImage] = useState<ProcessingResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rotation, setRotation] = useState(0);
  
  // Crop states
  // Future implementation: crop functionality
  // const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 100, height: 100 });
  const [showCropTool, setShowCropTool] = useState(false);
  
  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Image upload handler
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      // Basic validation
      if (file.size > 10 * 1024 * 1024) {
        setError('ファイルサイズは10MB以下にしてください。');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('画像ファイルを選択してください。');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target?.result as string;
        if (imageData && imageData.startsWith('data:image/')) {
          setSelectedImage(imageData);
          setError(null);
          setProcessedImage(null);
          setRotation(0);
          setShowCropTool(false);
        }
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp']
    },
    maxFiles: 1
  });

  // Image rotation
  const rotateImage = (degrees: number) => {
    setRotation(prev => (prev + degrees) % 360);
  };

  // Apply rotation and crop to image
  const applyTransformations = useCallback(() => {
    if (!selectedImage || !canvasRef.current || !imageRef.current) return null;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    if (!ctx) return null;

    // Set canvas size
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    // Apply rotation
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
    ctx.restore();

    return canvas.toDataURL('image/jpeg', 0.9);
  }, [selectedImage, rotation]);

  // Process image with AI
  const processImage = async () => {
    if (!selectedImage) return;

    setIsProcessing(true);
    setError(null);

    try {
      // Apply transformations first
      const transformedImage = applyTransformations();
      const imageToProcess = transformedImage || selectedImage;

      // Simple API call without authentication
      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT || 'https://processimage-72orsi5qmq-uc.a.run.app'}/processImage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_data: imageToProcess,
          quality: 'standard',
          model: 'openai', // Use OpenAI for simplicity
          anonymous: true // Mark as anonymous processing
        })
      });

      if (!response.ok) {
        throw new Error(`処理に失敗しました: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.downloadUrl) {
        setProcessedImage({
          downloadUrl: result.downloadUrl,
          processingTimeMs: result.processingTimeMs || 0
        });
      } else {
        throw new Error('処理結果が無効です。');
      }

    } catch (error: any) {
      console.error('Processing error:', error);
      setError(error.message || '画像処理中にエラーが発生しました。');
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset everything
  const reset = () => {
    setSelectedImage(null);
    setProcessedImage(null);
    setError(null);
    setRotation(0);
    setShowCropTool(false);
  };

  // Download processed image
  const downloadImage = async () => {
    if (!processedImage?.downloadUrl) return;

    try {
      const response = await fetch(processedImage.downloadUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `room-canvas-processed-${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      setError('ダウンロードに失敗しました。');
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Room Canvas</h1>
            <p>AI家具除去ツール - ワンクリックで空間をリフレッシュ</p>
          </div>
          <nav className="header-nav">
            <a href="/samples.html" className="samples-link">
              サンプルを見る
            </a>
          </nav>
        </div>
      </header>

      <main className="App-main">
        {/* Hero Section */}
        {!selectedImage && !processedImage && (
          <div className="hero-section">
            <h2>部屋の家具を瞬時に除去</h2>
            <p>AIの力で、お部屋の写真から家具を自動的に取り除きます。<br />
            不動産物件の撮影や、リフォームのイメージ作りに最適です。</p>
          </div>
        )}

        {error && (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={() => setError(null)} aria-label="閉じる">×</button>
          </div>
        )}

        {/* Image Upload Area */}
        <div className="upload-section">
          <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
            <input {...getInputProps()} />
            {selectedImage ? (
              <div className="image-preview">
                <img 
                  ref={imageRef}
                  src={selectedImage} 
                  alt="Selected" 
                  style={{ 
                    transform: `rotate(${rotation}deg)`,
                    maxWidth: '100%',
                    maxHeight: '400px'
                  }}
                />
                
                {/* Image Controls */}
                <div className="image-controls">
                  <button onClick={() => rotateImage(-90)} title="左に回転">
                    <span style={{ display: 'inline-block', transform: 'scaleX(-1)' }}>↻</span> 左回転
                  </button>
                  <button onClick={() => rotateImage(90)} title="右に回転">
                    ↻ 右回転
                  </button>
                  <button 
                    onClick={() => setShowCropTool(!showCropTool)}
                    className={showCropTool ? 'active' : ''}
                  >
                    ✂ 切り取り
                  </button>
                  <button onClick={reset} className="reset-btn">
                    × リセット
                  </button>
                </div>

                {/* Crop Tool */}
                {showCropTool && (
                  <div className="crop-controls">
                    <p>切り取り機能は開発中です</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="upload-prompt">
                {isDragActive ? (
                  <p>ファイルをドロップしてください</p>
                ) : (
                  <div>
                    <p>画像をドラッグ&ドロップまたはクリックして選択</p>
                    <small>JPG, PNG, WEBP対応 (最大10MB)</small>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Process Button */}
        {selectedImage && !processedImage && (
          <div className="process-section">
            <button 
              onClick={processImage}
              disabled={isProcessing}
              className="process-btn"
            >
              {isProcessing ? '処理中...' : '家具を除去する'}
            </button>
          </div>
        )}

        {/* Processing Progress */}
        {isProcessing && (
          <div className="processing-indicator">
            <div className="spinner"></div>
            <p>AI処理中です。しばらくお待ちください...</p>
          </div>
        )}

        {/* Processed Result */}
        {processedImage && (
          <div className="result-section">
            <h3>処理結果</h3>
            <div className="result-image">
              <img src={processedImage.downloadUrl} alt="Processed" />
            </div>
            <div className="result-actions">
              <button onClick={downloadImage} className="download-btn">
                <span>↓</span> ダウンロード
              </button>
              <button onClick={reset} className="reset-btn">
                新しい画像を処理
              </button>
            </div>
            {processedImage.processingTimeMs > 0 && (
              <small>処理時間: {(processedImage.processingTimeMs / 1000).toFixed(1)}秒</small>
            )}
          </div>
        )}

        {/* Hidden canvas for transformations */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </main>

      <footer className="App-footer">
        <p>© 2025 Room Canvas v{APP_VERSION} - AI搭載の家具除去ツール</p>
      </footer>
    </div>
  );
}

export default App;